import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { key: 'contact', label: 'Contato', icon: 'solar:letter-linear' },
  { key: 'franchisee', label: 'Franqueado', icon: 'solar:case-linear' },
  { key: 'job', label: 'Trabalhe Conosco', icon: 'solar:users-group-rounded-linear' },
];

const STATUS_LABEL = {
  new: 'Novo',
  in_review: 'Em análise',
  contacted: 'Contatado',
  archived: 'Arquivado',
};

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  in_review: 'bg-amber-100 text-amber-700',
  contacted: 'bg-green-100 text-green-700',
  archived: 'bg-neutral-100 text-neutral-500',
};

// Labels in PT-BR for known submission fields. Any field not in this
// map still renders in the details panel — the label is generated from
// the key so we never accidentally hide user-submitted data.
const FIELD_LABELS = {
  name: 'Nome',
  email: 'Email',
  phone: 'Telefone',
  city: 'Cidade',
  state: 'Estado',
  subject: 'Assunto',
  message: 'Mensagem',
  area: 'Área de interesse',
  resume_url: 'Currículo (arquivo)',
  status: 'Status',
  created_at: 'Recebido em',
  updated_at: 'Atualizado em',
  submission_id: 'ID',
};

// Keys never shown inside the details drawer (either purely internal or
// already presented in dedicated table columns above the drawer).
const HIDDEN_FIELDS = new Set(['submission_id', '_id', 'updated_at']);

const humanLabel = (key) =>
  FIELD_LABELS[key] ||
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const isUrl = (v) =>
  typeof v === 'string' && /^https?:\/\//i.test(v.trim());

// Resume URLs may be relative (`/api/public/resume/resume_xxx.pdf`) — we
// still want them rendered as clickable + inline-previewable, so match
// both absolute and relative forms.
const isResumeUrl = (v) =>
  typeof v === 'string' && /\/api\/public\/resume\//.test(v);

const isPdfHref = (v) =>
  typeof v === 'string' && /\.pdf(\?|$)/i.test(v);

const absolutizeApiUrl = (v) => {
  if (!v || /^https?:\/\//i.test(v)) return v;
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return v.startsWith('/') ? `${base}${v}` : `${base}/${v}`;
};

const ResumePreview = ({ href }) => {
  const abs = absolutizeApiUrl(href);
  const canEmbed = isPdfHref(abs);
  const [status, setStatus] = React.useState('checking'); // 'checking' | 'ok' | 'missing'

  React.useEffect(() => {
    let cancelled = false;
    fetch(abs, { method: 'HEAD' })
      .then((r) => { if (!cancelled) setStatus(r.ok ? 'ok' : 'missing'); })
      .catch(() => { if (!cancelled) setStatus('missing'); });
    return () => { cancelled = true; };
  }, [abs]);

  if (status === 'checking') {
    return (
      <div className="mt-1 text-xs text-zinc-700 italic" data-testid="resume-preview">
        Verificando arquivo…
      </div>
    );
  }

  if (status === 'missing') {
    return (
      <div
        className="mt-1 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
        data-testid="resume-preview-missing"
      >
        <div className="font-medium mb-1">Arquivo indisponível</div>
        <div className="text-xs text-amber-700">
          Este currículo foi enviado antes da migração de armazenamento e
          não pôde ser recuperado. Peça ao candidato para reenviar o arquivo.
        </div>
        <div className="text-[10px] font-mono text-amber-600 mt-2 break-all">{href}</div>
      </div>
    );
  }

  return (
    <div className="mt-1 space-y-2" data-testid="resume-preview">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <a
          href={abs}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"
        >
          <iconify-icon icon="solar:document-linear" className="text-base"></iconify-icon>
          Abrir em nova aba
        </a>
        <a
          href={abs}
          download
          className="inline-flex items-center gap-1.5 text-neutral-600 hover:text-black"
        >
          <iconify-icon icon="solar:download-linear" className="text-base"></iconify-icon>
          Baixar
        </a>
        <span className="text-xs font-mono text-zinc-700 break-all">{href}</span>
      </div>
      {canEmbed ? (
        <object
          data={abs}
          type="application/pdf"
          className="w-full h-[560px] rounded-lg border border-neutral-200 bg-neutral-50"
          data-testid="resume-preview-embed"
        >
          <div className="p-4 text-sm text-neutral-500">
            Não foi possível carregar o PDF inline. Use "Abrir em nova aba" acima.
          </div>
        </object>
      ) : (
        <div className="text-xs text-neutral-500 italic">
          Preview inline disponível apenas para PDF. Use "Baixar" ou "Abrir em nova aba".
        </div>
      )}
    </div>
  );
};

const renderValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'created_at' || key === 'updated_at') {
    try { return new Date(value).toLocaleString('pt-BR'); } catch { return String(value); }
  }
  if (key === 'email') return (<a href={`mailto:${value}`} className="text-blue-600 hover:underline break-all">{value}</a>);
  if (key === 'phone') return (<a href={`tel:${value}`} className="text-blue-600 hover:underline">{value}</a>);
  if (key === 'resume_url' || isResumeUrl(value)) return <ResumePreview href={value} />;
  if (isUrl(value)) return (<a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{value}</a>);
  return (<span className="whitespace-pre-wrap break-words">{String(value)}</span>);
};

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SubmissionsManager = () => {
  const [kind, setKind] = useState('contact');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [counts, setCounts] = useState({});
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const load = useCallback(async (k = kind) => {
    setLoading(true);
    try {
      const [{ data: list }, { data: c }] = await Promise.all([
        axios.get(`${API_URL}/admin/submissions/${k}`, { withCredentials: true, headers: authHeaders() }),
        axios.get(`${API_URL}/admin/submissions-counts`, { withCredentials: true, headers: authHeaders() }),
      ]);
      setItems(list);
      setCounts(c);
    } catch (err) {
      console.error('Failed to load submissions:', err);
      toast.error('Falha ao carregar submissões');
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load(kind);
  }, [kind, load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API_URL}/admin/submissions/${kind}/${id}`,
        { status },
        { withCredentials: true, headers: authHeaders() },
      );
      setItems((prev) => prev.map((it) => (it.submission_id === id ? { ...it, status } : it)));
      // Re-fetch counts so the tab badges stay in sync
      try {
        const { data: c } = await axios.get(`${API_URL}/admin/submissions-counts`, {
          withCredentials: true,
          headers: authHeaders(),
        });
        setCounts(c);
      } catch (err) {
        console.warn('Failed to refresh counts:', err?.message || err);
      }
      toast.success('Status atualizado');
    } catch (err) {
      console.error('Failed to update submission status:', err);
      toast.error('Falha ao atualizar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remover esta submissão?')) return;
    try {
      await axios.delete(`${API_URL}/admin/submissions/${kind}/${id}`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setItems((prev) => prev.filter((it) => it.submission_id !== id));
      try {
        const { data: c } = await axios.get(`${API_URL}/admin/submissions-counts`, {
          withCredentials: true,
          headers: authHeaders(),
        });
        setCounts(c);
      } catch (err) {
        console.warn('Failed to refresh counts after delete:', err?.message || err);
      }
      toast.success('Removida');
    } catch (err) {
      console.error('Failed to delete submission:', err);
      toast.error('Falha ao remover');
    }
  };

  const filtered = useMemo(() => {
    const q = filter.toLowerCase().trim();
    if (!q) return items;
    return items.filter((it) =>
      [it.name, it.email, it.phone, it.city, it.subject, it.message, it.area]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [filter, items]);

  return (
    <div className="space-y-6" data-testid="admin-submissions">
      <div>
        <h2 className="text-3xl font-bold mb-1">Submissões</h2>
        <p className="text-sm text-neutral-500">Leads recebidos pelos formulários públicos do site.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const c = counts[t.key] || { total: 0, new: 0 };
          const active = kind === t.key;
          return (
            <button
              key={t.key}
              data-testid={`submissions-tab-${t.key}`}
              onClick={() => setKind(t.key)}
              className={`px-5 py-3 rounded-xl flex items-center gap-3 border transition-all ${
                active ? 'bg-black text-white border-black' : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
              }`}
            >
              <iconify-icon icon={t.icon} className="text-xl"></iconify-icon>
              <span className="font-medium">{t.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${active ? 'bg-white text-black' : 'bg-neutral-100 text-neutral-700'}`}>
                {c.total}
              </span>
              {c.new > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">{c.new} novo</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <iconify-icon icon="solar:magnifer-linear" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-700 text-xl"></iconify-icon>
          <input
            type="text"
            placeholder="Buscar por nome, email, mensagem…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm outline-none focus:border-neutral-400"
            data-testid="submissions-search-input"
          />
        </div>
        <button
          onClick={() => load()}
          className="px-4 py-2.5 bg-white border border-neutral-200 rounded-lg text-sm hover:bg-neutral-50 flex items-center gap-2"
          data-testid="submissions-refresh-btn"
        >
          <iconify-icon icon="solar:refresh-linear"></iconify-icon>
          Atualizar
        </button>
        <a
          href={`${API_URL}/admin/submissions/${kind}/export`}
          onClick={async (e) => {
            // axios-style download with auth header (CSV endpoint)
            e.preventDefault();
            try {
              const res = await axios.get(`${API_URL}/admin/submissions/${kind}/export`, {
                withCredentials: true,
                headers: authHeaders(),
                responseType: 'blob',
              });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a');
              a.href = url;
              a.download = `submissions_${kind}_${new Date().toISOString().slice(0, 10)}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch (err) {
              console.error('Failed to export CSV:', err);
              toast.error('Falha ao exportar CSV');
            }
          }}
          className="px-4 py-2.5 bg-black text-white rounded-lg text-sm hover:bg-accent-orange transition-colors flex items-center gap-2 cursor-pointer"
          data-testid="submissions-export-csv-btn"
        >
          <iconify-icon icon="solar:download-linear"></iconify-icon>
          Exportar CSV
        </a>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-neutral-500">
            <div className="animate-spin h-8 w-8 border-2 border-black/20 border-t-black rounded-full mx-auto mb-3"></div>
            Carregando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-neutral-500">
            <iconify-icon icon="solar:inbox-linear" className="text-6xl mb-3"></iconify-icon>
            <p>Nenhuma submissão encontrada.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500 text-xs font-mono uppercase tracking-widest">
                <tr>
                  <th className="text-left px-3 py-3 w-8"></th>
                  <th className="text-left px-5 py-3">Data</th>
                  <th className="text-left px-5 py-3">Nome</th>
                  <th className="text-left px-5 py-3">Contato</th>
                  <th className="text-left px-5 py-3">Mensagem</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-right px-5 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {filtered.map((s) => {
                  const isOpen = expandedIds.has(s.submission_id);
                  const detailEntries = Object.entries(s).filter(
                    ([k, v]) => !HIDDEN_FIELDS.has(k) && v !== null && v !== undefined && v !== '',
                  );
                  return (
                  <React.Fragment key={s.submission_id}>
                  <tr className="hover:bg-neutral-50" data-testid={`submission-row-${s.submission_id}`}>
                    <td className="px-3 py-4 align-top">
                      <button
                        type="button"
                        onClick={() => toggleExpand(s.submission_id)}
                        aria-label={isOpen ? 'Recolher' : 'Ver detalhes'}
                        data-testid={`submission-expand-${s.submission_id}`}
                        className="p-1.5 rounded hover:bg-neutral-100 text-neutral-500"
                      >
                        <iconify-icon
                          icon="solar:alt-arrow-down-linear"
                          className={`text-lg transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        ></iconify-icon>
                      </button>
                    </td>
                    <td className="px-5 py-4 align-top whitespace-nowrap text-neutral-500">
                      {new Date(s.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-5 py-4 align-top">
                      <div className="font-medium text-black">{s.name}</div>
                      {s.city && <div className="text-xs text-neutral-500">{s.city}</div>}
                      {s.area && <div className="text-xs text-neutral-500">{s.area}</div>}
                      {s.subject && <div className="text-xs text-neutral-500">{s.subject}</div>}
                    </td>
                    <td className="px-5 py-4 align-top">
                      <a href={`mailto:${s.email}`} className="text-blue-600 hover:underline block">{s.email}</a>
                      {s.phone && <a href={`tel:${s.phone}`} className="text-neutral-500 text-xs">{s.phone}</a>}
                    </td>
                    <td className="px-5 py-4 align-top max-w-md">
                      <button
                        type="button"
                        onClick={() => toggleExpand(s.submission_id)}
                        className="text-left text-neutral-700 line-clamp-3 hover:text-black w-full cursor-pointer"
                        title="Clique para ver a mensagem completa"
                      >
                        {s.message || '—'}
                      </button>
                    </td>
                    <td className="px-5 py-4 align-top">
                      <select
                        value={s.status}
                        onChange={(e) => updateStatus(s.submission_id, e.target.value)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border-0 outline-none cursor-pointer ${STATUS_COLORS[s.status] || ''}`}
                        data-testid={`submission-status-${s.submission_id}`}
                      >
                        {Object.entries(STATUS_LABEL).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4 align-top text-right">
                      <button
                        onClick={() => remove(s.submission_id)}
                        className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remover"
                        data-testid={`submission-delete-${s.submission_id}`}
                      >
                        <iconify-icon icon="solar:trash-bin-trash-linear" className="text-lg"></iconify-icon>
                      </button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-neutral-50" data-testid={`submission-details-${s.submission_id}`}>
                      <td colSpan={7} className="px-5 py-5">
                        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3">
                          Resposta completa do lead
                        </div>
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                          {detailEntries.map(([k, v]) => (
                            <div key={k} className={(k === 'message' || k === 'resume_url' || isResumeUrl(v)) ? 'md:col-span-2' : ''}>
                              <dt className="text-[11px] font-mono uppercase tracking-widest text-neutral-500">
                                {humanLabel(k)}
                              </dt>
                              <dd className="text-sm text-black mt-0.5">{renderValue(k, v)}</dd>
                            </div>
                          ))}
                          <div className="md:col-span-2 pt-2 border-t border-neutral-200">
                            <dt className="text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-1">ID interno</dt>
                            <dd className="text-xs font-mono text-zinc-700 break-all">{s.submission_id}</dd>
                          </div>
                        </dl>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmissionsManager;
